import { computed, makeObservable, observable, runInAction } from "mobx"
import { formatUnits, parseUnits } from "viem"

import type { ClFarmStoreDAO, MintToken, ProviderLpToken, ScalensClFarmData } from "@/domains/cl-farm/types"
import type { IGauge, IRevenueSource } from "@/domains/gauge"
import type { ILiquidityPool, ILiquidityPoolType } from "@/domains/pools/types"
import type { TokenStore } from "@/domains/tokens"
import { LcAddress } from "@/domains/core"

import { BIGINT_ZERO } from "../on-chain"

export class ClFarmStore implements ILiquidityPool, IRevenueSource {
  address: LcAddress
  alm: string
  analytics: string
  apiApr: number = 0
  bribeAddress: LcAddress | null
  buck1?: number
  clPoolAddress: LcAddress
  decimals: number
  description: string
  dex: string
  displayName: string
  displayType: string
  docs: string
  extendedType: string
  farmAddress: LcAddress
  fee: number = 0
  feeAddress: LcAddress | null
  flipLensTokens: boolean
  connectedGauge: IGauge | undefined = undefined // Connected gauge store for bidirectional mapping
  gaugeAddress: LcAddress | null
  guardAddress?: LcAddress
  isProductionReady: boolean
  isSingleSided: boolean
  isStable: boolean
  mintTokens?: Array<MintToken>
  name: string
  onChainApr: bigint = 0n
  onChainGaugeReserve0: bigint = 0n
  onChainGaugeReserve1: bigint = 0n
  onChainGaugeTotalSupply: bigint = 0n
  onChainReserve0: bigint = 0n
  onChainReserve1: bigint = 0n
  onChainTotalSupply: bigint = 0n
  onChainTvl: bigint = 0n
  onChainUserBalance: bigint = 0n
  onChainUserBalanceToken0: bigint = 0n
  onChainUserBalanceToken1: bigint = 0n
  onChainUserClaimableFees0: bigint = 0n
  onChainUserClaimableFees1: bigint = 0n
  onChainUserGaugeBalance: bigint = 0n
  onChainUserGaugeGovernanceEarned: bigint = 0n
  onChainUserGaugeReserve0: bigint = 0n
  onChainUserGaugeReserve1: bigint = 0n
  pool?: string
  providerApp: string
  providerLp: ProviderLpToken
  slipBps?: number
  tags: string[]
  token0: TokenStore
  token1: TokenStore
  tokenEarn: TokenStore
  vaultAddress: LcAddress
  vdep?: string
  voterAddress: LcAddress

  constructor(data: ClFarmStoreDAO) {
    this.address = data.address
    this.alm = data.alm
    this.analytics = data.analytics
    this.apiApr = data.apr
    this.bribeAddress = data.bribeAddress
    this.buck1 = data.buck1
    this.clPoolAddress = data.clPoolAddress
    this.decimals = data.decimals
    this.description = data.description
    this.dex = data.dex
    this.displayName = data.displayName
    this.displayType = data.displayType
    this.docs = data.docs
    this.extendedType = data.extendedType
    this.farmAddress = data.farmAddress
    this.fee = data.fee
    this.feeAddress = data.feeAddress
    this.flipLensTokens = data.flipLensTokens
    this.gaugeAddress = data.gaugeAddress
    this.guardAddress = data.guardAddress
    this.isProductionReady = data.isProductionReady
    this.isSingleSided = data.isSingleSided
    this.isStable = data.isStable
    this.mintTokens = data.mintTokens
    this.name = data.name
    this.pool = data.pool
    this.providerApp = data.providerApp
    this.providerLp = data.providerLp
    this.slipBps = data.slipBps
    this.tags = data.tags
    this.token0 = data.token0
    this.token1 = data.token1
    this.tokenEarn = data.tokenEarn
    this.vaultAddress = data.vaultAddress
    this.vdep = data.vdep
    this.voterAddress = data.voterAddress

    makeObservable(this, {
      apiApr: observable,
      description: observable,
      displayName: observable,
      displayType: observable,
      fee: observable,
      isProductionReady: observable,
      onChainApr: observable,
      onChainGaugeReserve0: observable,
      onChainGaugeReserve1: observable,
      onChainGaugeTotalSupply: observable,
      onChainReserve0: observable,
      onChainReserve1: observable,
      onChainTotalSupply: observable,
      onChainTvl: observable,
      onChainUserBalance: observable,
      onChainUserBalanceToken0: observable,
      onChainUserBalanceToken1: observable,
      onChainUserClaimableFees0: observable,
      onChainUserClaimableFees1: observable,
      onChainUserGaugeBalance: observable,
      onChainUserGaugeGovernanceEarned: observable,
      onChainUserGaugeReserve0: observable,
      onChainUserGaugeReserve1: observable,
      tags: observable,
      token0: observable,
      token1: observable,
      tokenEarn: observable,
      connectedGauge: observable,

      // Computed properties
      apr: computed,
      displayApr: computed,
      displayBalance: computed,
      displayFee: computed,
      displayIncomePerEpoch: computed,
      displayTotalIncentivesUsd: computed,
      gaugeReserves: computed,
      gaugeTotalSupplyPercentage: computed,
      gaugeValueUSD: computed,
      price: computed,
      reserves: computed,
      tokens: computed,
      totalIncentivesUsd: computed,
      tokenPercentages: computed,
      tvlUsd: computed,
      type: computed,
      userBalance: computed,
      userBalancePercentage: computed,
      userBalanceToken0: computed,
      userBalanceToken1: computed,
      userBalanceValueUSD: computed,
      walletValueUSD: computed,
    })
  }

  get apr(): number {
    return this.onChainApr > 0n ? Number(this.formatUnits(this.onChainApr)) : this.apiApr
  }

  /**
   * Calculates a percentage of the user wallet balance
   * @param percentage (0-1) for 0-100%
   * @returns
   */
  calculateValuePercentage(value: bigint, percentage: number): string {
    // Convert percentage to BigInt (multiply by 10000 to preserve 4 decimal places)
    const precision = 10000
    const percentageBigInt = BigInt(Math.floor(percentage * precision))

    // Calculate: (balance * percentage) / 10000
    const result = (value * percentageBigInt) / BigInt(precision)

    // format to this tokens decimals
    return this.formatUnits(result)
  }

  calculateValueUSD(amount: number): number {
    return amount * this.price
  }

  get price(): number {
    return this.totalSupply > 0 ? this.tvlUsd / this.totalSupply : 0
  }

  get displayApr(): string {
    if (this.onChainApr > 0n) {
      const chainAprNumber = Number(this.formatUnits(this.onChainApr))
      // console.log("chainAprNumber", chainAprNumber, this.apiApr)

      return `${chainAprNumber.toFixed(2)}%`
    }
    return `${this.apr.toFixed(2)}%`
  }

  get displayBalance(): string {
    return this.formatUnits(this.onChainUserBalance)
  }

  get displayFee(): string {
    return `${(this.fee * 100).toFixed(4)}%`
  }

  get displayIncomePerEpoch(): string {
    return `$${(((this.gaugeValueUSD * this.apr) / 100 / 365) * 7).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}/epoch`
  }

  get displayTotalIncentivesUsd(): string {
    return `$${this.totalIncentivesUsd.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  }

  get gaugeReserves(): Array<bigint> {
    return [this.onChainGaugeReserve0, this.onChainGaugeReserve1]
  }

  get gaugeTotalSupplyPercentage(): number {
    return Number(this.formatUnits(this.onChainGaugeTotalSupply)) / Number(this.formatUnits(this.onChainTotalSupply))
  }

  get gaugeUserBalance(): bigint {
    return this.onChainUserGaugeBalance
  }

  get gaugeUserBalancePercentage(): number {
    return (
      Number(this.formatUnits(this.onChainUserGaugeBalance)) / Number(this.formatUnits(this.onChainGaugeTotalSupply))
    )
  }

  get gaugeUserBalanceToken0(): bigint {
    return this.onChainUserGaugeReserve0
  }

  get gaugeUserBalanceToken1(): bigint {
    return this.onChainUserGaugeReserve1
  }

  get gaugeUserBalanceValueUSD(): number {
    return (
      Number(this.token0.formatUnits(this.gaugeUserBalanceToken0)) * this.token0.bestPrice +
      Number(this.token1.formatUnits(this.gaugeUserBalanceToken1)) * this.token1.bestPrice
    )
  }

  get gaugeUserValueUSD(): number {
    return (
      Number(this.token0.formatUnits(this.gaugeUserBalanceToken0)) * this.token0.bestPrice +
      Number(this.token1.formatUnits(this.gaugeUserBalanceToken1)) * this.token1.bestPrice
    )
  }

  get gaugeValueUSD(): number {
    return (
      Number(this.token0.formatUnits(this.onChainGaugeReserve0)) * this.token0.bestPrice +
      Number(this.token1.formatUnits(this.onChainGaugeReserve1)) * this.token1.bestPrice
    )
  }

  get imageURL(): string {
    return this.mintTokens?.[0]?.token.imageURL ?? ""
  }

  get reserves(): Array<bigint> {
    return [this.onChainReserve0, this.onChainReserve1]
  }

  get symbol(): string {
    return this.displayName
  }

  get tokens(): Array<TokenStore> {
    return [this.token0, this.token1]
  }

  get totalIncentivesUsd(): number {
    if (!this.connectedGauge) return 0

    return this.connectedGauge.incentives.reduce((total, incentive) => total + incentive.rewardAmountUsd, 0)
  }

  get tokenPercentages(): { token0Percentage: number; token1Percentage: number } {
    const token0ReserveValue = Number(this.token0.formatUnits(this.onChainReserve0)) * this.token0.bestPrice
    const token1ReserveValue = Number(this.token1.formatUnits(this.onChainReserve1)) * this.token1.bestPrice

    const totalReserveValue = token0ReserveValue + token1ReserveValue
    const token0Percentage = totalReserveValue > 0 ? (token0ReserveValue / totalReserveValue) * 100 : 0
    const token1Percentage = 100 - token0Percentage
    return { token0Percentage, token1Percentage }
  }

  get totalSupply(): number {
    if (this.onChainTotalSupply > 0n) {
      return this.formatUnitsAsNumber(this.onChainTotalSupply)
    }
    // @todo: add api total supply
    // if (this.apiTotalSupply > 0) {
    //   return this.formatUnits(this.apiTotalSupply)
    // }
    return 0
  }

  get tvlUsd(): number {
    const onChainTvl = this.onChainTvl > 0n ? Number(this.formatUnits(this.onChainTvl)) : 0
    const calculatedTvl =
      Number(this.token0.formatUnits(this.onChainReserve0)) * this.token0.bestPrice +
      Number(this.token1.formatUnits(this.onChainReserve1)) * this.token1.bestPrice

    // console.log("tvlUsd", calculatedTvl, onChainTvl)

    // if (onChainTvl > 0n) return onChainTvl

    return calculatedTvl
  }

  get type(): ILiquidityPoolType {
    return this.isStable ? "stable" : "volatile"
  }

  get userBalance(): bigint {
    return this.onChainUserBalance
  }

  get userBalancePercentage(): number {
    return Number(this.formatUnits(this.userBalance)) / Number(this.totalSupply)
  }

  get userBalanceToken0(): bigint {
    return this.onChainUserBalanceToken0
  }

  get userBalanceToken1(): bigint {
    return this.onChainUserBalanceToken1
  }

  get userBalanceValueUSD(): number {
    return (
      Number(this.token0.formatUnits(this.userBalanceToken0)) * this.token0.bestPrice +
      Number(this.token1.formatUnits(this.userBalanceToken1)) * this.token1.bestPrice
    )
  }

  get walletValueUSD(): number {
    return (
      Number(this.token0.displayBalance) * this.token0.bestPrice +
      Number(this.token1.displayBalance) * this.token1.bestPrice
    )
  }

  /**
   * Pool specific unit formatting (uses internal decimals)
   * @param wei
   * @returns
   */
  formatUnitsAsNumber(wei: bigint): number {
    return Number(formatUnits(wei, this.decimals))
  }

  /**
   * Pool specific unit formatting (uses internal decimals)
   * @param wei
   * @returns
   */
  formatUnits(wei: bigint): string {
    return formatUnits(wei, this.decimals)
  }

  parseUnits(wei: string): bigint {
    return parseUnits(wei, this.decimals)
  }

  // Update static data (useful for refreshing from API)
  updateStaticData(data: Partial<ClFarmStoreDAO>): void {
    // if (data.displayName === "CL-WFTM/axlUSDC-0.30% (wFTM Ichi)") {
    // console.log("data", data)
    // }

    // Update fields that can change
    // only absolutely vital things here, not everything should update
    if (data.description !== undefined) this.description = data.description
    if (data.displayName !== undefined) this.displayName = data.displayName
    if (data.fee !== undefined) this.fee = data.fee
    if (data.tags !== undefined) this.tags = data.tags
  }

  /**
   * Converts the current CLFarm store to the required Lens params format
   * @param who LcAddress of the user or safeAddress
   * @returns
   */
  getLensParams(who: LcAddress): [LcAddress, LcAddress, LcAddress] | undefined {
    this.alm
    if (this.alm === "Fertilizer") {
      return [this.farmAddress, who, this.vaultAddress]
    }
    if (this.alm === "Ichi" && this.guardAddress) {
      return [this.farmAddress, who, this.guardAddress]
    }
    if (this.alm === "Gamma" && this.guardAddress) {
      return [this.farmAddress, who, this.guardAddress]
    }
    if (this.alm === "Steer") {
      return [this.farmAddress, who, this.vaultAddress]
    }
    return undefined
  }

  updateLensStaticData(values: bigint[]): void {
    const processed = this.normalizeRawScalensData(values)

    // if (this.address.equalsString("0xe20108aEDFC6D27763E04a4bD8E20384462DAC8a")) {
    //   console.log("processed", processed)
    //   console.log("this.displayName", this.displayName)
    // }

    this.onChainApr = processed.apr
    this.onChainReserve0 = processed.reserve0
    this.onChainReserve1 = processed.reserve1
    this.onChainTotalSupply = processed.totalSupply
    // this.onChainUserBalance = processed.userToken0Balance
    this.onChainUserBalanceToken0 = processed.userToken0Balance
    this.onChainUserBalanceToken1 = processed.userToken1Balance

    this.onChainGaugeTotalSupply = processed.totalStaked
    this.onChainTvl = processed.tvl
    this.onChainGaugeReserve0 =
      processed.totalSupply === 0n ? 0n : (processed.totalStaked * processed.reserve0) / processed.totalSupply

    this.onChainGaugeReserve1 =
      processed.totalSupply === 0n ? 0n : (processed.totalStaked * processed.reserve1) / processed.totalSupply
    // onChainGaugeReserve1: observable,

    // its the userAlmWalletBalance that determines how many tokens are in the wallet `My Pool` column
    // onChainUserBalance: observable,
    // onChainUserBalanceToken0: observable,
    // onChainUserBalanceToken1: observable,
    this.onChainUserBalance = processed.userAlmWalletBalance
    this.onChainUserBalanceToken0 =
      processed.totalSupply === 0n ? 0n : (processed.userAlmWalletBalance * processed.reserve0) / processed.totalSupply
    this.onChainUserBalanceToken1 =
      processed.totalSupply === 0n ? 0n : (processed.userAlmWalletBalance * processed.reserve1) / processed.totalSupply

    // its the userAlmStakedBalance that determines how many tokens are in the wallet `My Staked` column
    // onChainUserGaugeBalance: observable,
    // onChainUserGaugeReserve0: observable,
    // onChainUserGaugeReserve1: observable,
    this.onChainUserGaugeBalance = processed.userAlmStakedBalance
    this.onChainUserGaugeReserve0 =
      processed.totalSupply === 0n ? 0n : (processed.userAlmStakedBalance * processed.reserve0) / processed.totalSupply
    this.onChainUserGaugeReserve1 =
      processed.totalSupply === 0n ? 0n : (processed.userAlmStakedBalance * processed.reserve1) / processed.totalSupply

    // onChainUserClaimableFees0: observable,
    // onChainUserClaimableFees1: observable,
    // onChainUserGaugeGovernanceEarned: observable,
  }

  /**
   * This is required to map the raw Scalens data to the ScalensClFarmData format
   * taking the farms flipLensTokens into account.
   *
   * This is because some cl farms return this values reversed (e.g. Ichi)
   *
   * @param values bigint[] from Scalens call
   * @returns ScalensClFarmData normalized to the farm's flipLensTokens
   */
  normalizeRawScalensData = (values: bigint[]): ScalensClFarmData => {
    const reserve0 = this.flipLensTokens ? values?.[1] : values?.[0]
    const reserve1 = this.flipLensTokens ? values?.[0] : values?.[1]
    const userToken0Balance = this.flipLensTokens ? values?.[7] : values?.[6]
    const userToken1Balance = this.flipLensTokens ? values?.[6] : values?.[7]
    const userToken0GuardAllowance = this.flipLensTokens ? values?.[11] : values?.[10]
    const userToken1GuardAllowance = this.flipLensTokens ? values?.[10] : values?.[11]
    return {
      reserve0: reserve0 ?? BIGINT_ZERO,
      reserve1: reserve1 ?? BIGINT_ZERO,
      totalSupply: values?.[2] ?? BIGINT_ZERO,
      totalStaked: values?.[3] ?? BIGINT_ZERO,
      tvl: values?.[4] ?? BIGINT_ZERO,
      apr: values?.[5] ?? BIGINT_ZERO,
      userToken0Balance: userToken0Balance ?? BIGINT_ZERO,
      userToken1Balance: userToken1Balance ?? BIGINT_ZERO,
      userAlmWalletBalance: values?.[8] ?? BIGINT_ZERO,
      userAlmStakedBalance: values?.[9] ?? BIGINT_ZERO,
      userToken0GuardAllowance: userToken0GuardAllowance ?? BIGINT_ZERO,
      userToken1GuardAllowance: userToken1GuardAllowance ?? BIGINT_ZERO,
      userAlmFarmAllowance: values?.[12] ?? BIGINT_ZERO,
    }
  }

  /**
   * Connect this CL farm to a gauge store
   * Also connects the gauge to this farm as a revenue source (bidirectional)
   */
  connectToGauge(gauge: IGauge): void {
    runInAction(() => {
      this.connectedGauge = gauge
      // Also connect the gauge to this farm as a revenue source
      // Pass 'this' directly to maintain MobX reactivity - the gauge will observe changes to this store
      gauge.connectToRevenueSource(this)
    })
  }
}
